import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Alert, Spinner, Badge, Form, ListGroup, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { getSystemActivities, saveAllAccountsHistory } from '../services/api';
import { formatDateTime } from '../utils/dateUtils';

function ActivityLog() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    activityType: '',
    days: 7,
    status: ''
  });

  // Check if user is admin, redirect if not
  useEffect(() => {
    const isAdmin = localStorage.getItem('isAdmin') === 'true';
    if (!isAdmin) {
      navigate('/dashboard', { replace: true });
    }
  }, [navigate]);

  const fetchActivities = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const params = {
        days: filters.days
      };
      
      if (filters.activityType) {
        params.activity_type = filters.activityType;
      }
      
      if (filters.status) {
        params.status = filters.status;
      }
      
      const response = await getSystemActivities(params);
      
      if (response.data && response.data.success) {
        setActivities(response.data.activities || []);
      } else {
        setError('Failed to load activity log');
      }
    } catch (error) {
      console.error('Error loading system activities:', error);
      setError('Failed to load activity log. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Load activities on component mount and when filters change
  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  // formatDateTime function is now imported from dateUtils

  // Get badge color based on status
  const getStatusBadge = (status) => {
    switch (status) {
      case 'success':
        return <Badge bg="success">Success</Badge>;
      case 'partial_success':
        return <Badge bg="warning" text="dark">Partial Success</Badge>;
      case 'warning':
        return <Badge bg="warning" text="dark">Warning</Badge>;
      case 'error':
      case 'failed':
        return <Badge bg="danger">Failed</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  // Get nice display name for activity type
  const getActivityTypeDisplay = (type) => {
    switch (type) {
      case 'daily_balance_save':
        return 'Daily Balance Save';
      case 'daily_balance_verification':
        return 'Balance Verification';
      default:
        return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    }
  };

  // Handle filter changes
  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Get activity detail for expanded view
  const getActivityDetails = (details) => {
    if (!details) return null;
    
    return (
      <div className="mt-2">
        <small className="text-muted">
          {details.success_count !== undefined && (
            <div>Accounts updated: {details.success_count} of {details.total_accounts}</div>
          )}
          {details.error && (
            <div className="text-danger">Error: {details.error}</div>
          )}
        </small>
      </div>
    );
  };

  // Handle manual balance save
  const handleSaveAllBalances = async () => {
    try {
      setSaveLoading(true);
      setSaveMessage(null);
      
      const response = await saveAllAccountsHistory();
      
      // Set success message
      setSaveMessage({
        type: 'success',
        text: response.data.message || 'All account balances saved successfully to history.'
      });
      
      // Reload activities to show the new activity
      fetchActivities();
    } catch (error) {
      console.error('Error saving account balances:', error);
      
      // Set error message
      setSaveMessage({
        type: 'danger',
        text: error.response?.data?.error || 'Failed to save account balances to history.'
      });
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <Container>
      {/* Page Header */}
      <Row className="mt-3 mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="mb-0">System Activity Log</h2>
              <p className="text-muted">
                View the history of automated system tasks including daily balance updates
              </p>
            </div>
            <div>
              <Button 
                variant="primary" 
                onClick={handleSaveAllBalances}
                disabled={saveLoading}
              >
                {saveLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving Balances...
                  </>
                ) : (
                  <>Save All Account Balances</>
                )}
              </Button>
            </div>
          </div>
          
          {/* Save Feedback Message */}
          {saveMessage && (
            <Alert 
              variant={saveMessage.type} 
              dismissible 
              onClose={() => setSaveMessage(null)}
              className="mt-3"
            >
              {saveMessage.text}
            </Alert>
          )}
        </Col>
      </Row>
      
      {/* Filter Card */}
      <Card className="mb-3 shadow-sm">
        <Card.Body className="py-2">
          <div className="row g-2">
            <div className="col-md-4">
              <div className="d-flex align-items-center">
                <Form.Label className="mb-0 me-2 small text-nowrap">Type:</Form.Label>
                <Form.Select 
                  size="sm"
                  value={filters.activityType}
                  onChange={e => handleFilterChange('activityType', e.target.value)}
                  className="w-100"
                >
                  <option value="">All Activities</option>
                  <option value="daily_balance_save">Daily Balance Save</option>
                  <option value="daily_balance_verification">Balance Verification</option>
                </Form.Select>
              </div>
            </div>
            
            <div className="col-md-4">
              <div className="d-flex align-items-center">
                <Form.Label className="mb-0 me-2 small text-nowrap">Status:</Form.Label>
                <Form.Select 
                  size="sm"
                  value={filters.status}
                  onChange={e => handleFilterChange('status', e.target.value)}
                  className="w-100"
                >
                  <option value="">All Statuses</option>
                  <option value="success">Success</option>
                  <option value="partial_success">Partial Success</option>
                  <option value="warning">Warning</option>
                  <option value="failed">Failed</option>
                  <option value="error">Error</option>
                </Form.Select>
              </div>
            </div>
            
            <div className="col-md-4">
              <div className="d-flex align-items-center">
                <Form.Label className="mb-0 me-2 small text-nowrap">Period:</Form.Label>
                <Form.Select 
                  size="sm"
                  value={filters.days}
                  onChange={e => handleFilterChange('days', parseInt(e.target.value))}
                  className="w-100"
                >
                  <option value="1">Last 24 hours</option>
                  <option value="7">Last 7 days</option>
                  <option value="30">Last 30 days</option>
                  <option value="90">Last 90 days</option>
                </Form.Select>
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>
      
      {/* Activity Cards */}
      <Card>
        <Card.Body className="p-0">
          {/* Loading state */}
          {loading && (
            <div className="text-center my-3">
              <Spinner animation="border" variant="primary" size="sm" />
              <p className="mt-2 small">Loading activity log...</p>
            </div>
          )}
          
          {/* Error message */}
          {error && (
            <Alert variant="danger" className="m-2 py-2">{error}</Alert>
          )}
          
          {/* No activities */}
          {!loading && !error && activities.length === 0 && (
            <Alert variant="info" className="m-2 py-2">
              No system activities found for the selected filters.
            </Alert>
          )}
          
          {/* Activities list */}
          {!loading && !error && activities.length > 0 && (
            <ListGroup variant="flush" className="border-0">
              {activities.map(activity => (
                <ListGroup.Item key={activity.activity_id} className="py-2 border-start-0 border-end-0">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <div className="d-flex align-items-center">
                        <h6 className="mb-1">{getActivityTypeDisplay(activity.activity_type)}</h6>
                        <div className="ms-2">{getStatusBadge(activity.status)}</div>
                      </div>
                      <p className="mb-1 small">{activity.description}</p>
                      {getActivityDetails(activity.details)}
                    </div>
                    <div className="text-end">
                      <div className="text-muted small">
                        {formatDateTime(activity.timestamp)}
                      </div>
                    </div>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}

export default ActivityLog; 